import React, { useCallback, useMemo, useState } from "react";
import {
    Stack,
    Title,
    Text,
    Table,
    TextInput,
    Button,
    Pagination,
    Group,
    ScrollArea,
    TableThead,
    TableTbody,
    TableTr,
    TableTh,
    TableTd,
    Select,
    MultiSelect,
    rem,
    Modal,
    Alert,
    Loader,
    Center,
    Popover,
    Paper,
    Box,
    Badge,
    LoadingOverlay,
    Flex,
    UnstyledButton,
    ActionIcon
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    MdArrowDownward,
    MdArrowUpward,
    MdSearch,
    MdClose,
    MdFileDownload,
    MdCalendarToday,
    MdThumbUpOffAlt,
    MdThumbDownOffAlt,
    MdOutlineFeedback,
    MdRefresh,
    MdOpenInNew
} from "react-icons/md";
import { useMsal } from "@azure/msal-react";

import { getToken, useLogin } from "../../authConfig";
import { getFeedbackApi, exportFeedbackCsvApi, type FailureType, type FeedbackListItem, type FeedbackReason } from "../../api";
import { MdCheckCircle, MdHourglassEmpty } from "react-icons/md";
import { TimezoneDateDisplay } from "../common/TimezoneDateDisplay";
import { TimezoneUtils } from "../../utils/timezoneUtils";
import { FAILURE_TYPES, isFailureType } from "../../utils/feedbackFailureTypes";
import { DomainBadge } from "../domain-selector";
import { LoadErrorAlert } from "../common/LoadErrorAlert";
import { useAbortableFetch } from "../../hooks/useAbortableFetch";

const REASON_LABELS: Record<FeedbackReason, string> = {
    incorrect_answer: "Incorrect answer",
    incomplete_answer: "Incomplete answer",
    unsuitable_asset: "Unsuitable asset"
};

type ReviewStatusFilter = "reviewed" | "not_reviewed" | "all";

const isValidReviewStatusFilter = (value: string | null): value is ReviewStatusFilter => value === "reviewed" || value === "not_reviewed" || value === "all";

const FEEDBACK_COLUMNS: Array<{
    key: keyof FeedbackListItem;
    label: string;
    width: number;
    flexible: boolean;
    sortable?: boolean;
}> = [
    { key: "name", label: "User", width: 280, flexible: false, sortable: true },
    { key: "user_country", label: "Country", width: 100, flexible: false, sortable: true },
    { key: "domain", label: "Domain", width: 100, flexible: false },
    { key: "feedback_target", label: "Target", width: 260, flexible: true, sortable: true },
    { key: "feedback_type", label: "Type", width: 160, flexible: false, sortable: true },
    { key: "feedback_text", label: "Feedback", width: 300, flexible: true, sortable: true },
    { key: "timestamp", label: "Submitted", width: 220, flexible: false, sortable: true },
    { key: "review_status", label: "Status", width: 140, flexible: false, sortable: false },
    { key: "failure_type", label: "Failure Type", width: 160, flexible: false, sortable: false },
    { key: "reason", label: "User Reason", width: 160, flexible: false, sortable: false }
];

export interface FeedbackTableProps {
    userId?: string;
    title?: string;
    showExport?: boolean;
    showSearch?: boolean;
    showDateFilter?: boolean;
    showRefresh?: boolean;
    compact?: boolean;
    maxHeight?: string;
}

export const FeedbackTable: React.FC<FeedbackTableProps> = ({
    userId,
    title = "Feedback Inbox",
    showExport = true,
    showSearch = true,
    showDateFilter = true,
    showRefresh = true,
    compact = false,
    maxHeight = "60vh"
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState(() => (userId ? "" : searchParams.get("search")) || "");
    const [search, setSearch] = useState(() => (userId ? "" : searchParams.get("search")) || "");

    const [datePickerOpened, setDatePickerOpened] = useState(false);
    const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
        if (userId) return [null, null];
        const startDateParam = searchParams.get("startDate");
        const endDateParam = searchParams.get("endDate");
        if (startDateParam && endDateParam) {
            return [new Date(startDateParam), new Date(endDateParam)];
        }
        return [null, null];
    });

    const [reviewStatusFilter, setReviewStatusFilter] = useState<ReviewStatusFilter>(() => {
        const fromUrl = userId ? null : searchParams.get("reviewStatus");
        return isValidReviewStatusFilter(fromUrl) ? fromUrl : "all";
    });

    const [failureTypeFilter, setFailureTypeFilter] = useState<FailureType[]>(() => {
        if (userId) return [];
        const raw = searchParams.get("failureTypes");
        return raw ? raw.split(",").filter(isFailureType) : [];
    });

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [modalStartDate, setModalStartDate] = useState<string>("");
    const [modalEndDate, setModalEndDate] = useState<string>("");
    const [exportStatus, setExportStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [exportError, setExportError] = useState<string>("");

    const [sortBy, setSortBy] = useState<keyof FeedbackListItem>(() => (userId ? null : (searchParams.get("sortBy") as keyof FeedbackListItem)) || "timestamp");
    const [sortAsc, setSortAsc] = useState(() => {
        if (userId) return false;
        const sortAscParam = searchParams.get("sortAsc");
        return sortAscParam !== null ? sortAscParam === "true" : false;
    });
    const [page, setPage] = useState(() => (userId ? 0 : Number(searchParams.get("page"))) || 1);
    const [pageSize, setPageSize] = useState(() => (userId ? 0 : Number(searchParams.get("pageSize"))) || 15);

    const navigate = useNavigate();
    const client = useLogin ? useMsal().instance : undefined;

    const updateSearchParams = (updates: Record<string, string | number | boolean>) => {
        if (userId) return; // Don't update URL params when filtering by user

        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });
        setSearchParams(newParams);
    };

    const formatDateRange = () => {
        const [startDate, endDate] = dateRange;
        if (!startDate || !endDate) return null;
        const options: Intl.DateTimeFormatOptions = {
            month: "short",
            day: "numeric",
            year: startDate.getFullYear() !== endDate.getFullYear() ? "numeric" : undefined
        };
        return `${startDate.toLocaleDateString("en-US", options)} - ${endDate.toLocaleDateString("en-US", options)}`;
    };

    const applyDateRange = () => {
        if (tempStartDate && tempEndDate) {
            const startOfDay = new Date(tempStartDate);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(tempEndDate);
            endOfDay.setHours(23, 59, 59, 999);
            setDateRange([startOfDay, endOfDay]);
            setDatePickerOpened(false);
            updateSearchParams({
                startDate: startOfDay.toISOString(),
                endDate: endOfDay.toISOString()
            });
        }
    };

    const clearDateRange = () => {
        setDateRange([null, null]);
        setTempStartDate(null);
        setTempEndDate(null);
        setDatePickerOpened(false);
        updateSearchParams({ startDate: "", endDate: "" });
    };

    const fetchFeedback = useCallback(
        async (signal: AbortSignal) => {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available for fetching feedback");

            const [startDate, endDate] = dateRange;
            const startDateISO = startDate ? TimezoneUtils.formatDateForAPI(startDate) : undefined;
            const endDateISO = endDate ? TimezoneUtils.formatDateForAPI(endDate) : undefined;

            return getFeedbackApi(
                idToken,
                page,
                pageSize,
                sortBy,
                sortAsc ? "asc" : "desc",
                search,
                startDateISO,
                endDateISO,
                userId,
                TimezoneUtils.getUserTimezone(),
                reviewStatusFilter,
                failureTypeFilter.length > 0 ? failureTypeFilter : undefined,
                signal
            );
        },
        [client, page, pageSize, sortBy, sortAsc, search, dateRange, userId, reviewStatusFilter, failureTypeFilter]
    );

    const { data, loading, error, reload: fetchData } = useAbortableFetch(fetchFeedback, "Failed to fetch feedback");
    const feedback = useMemo<FeedbackListItem[]>(() => data?.feedback || [], [data]);
    const totalPages = data?.total_pages || 1;
    const totalItems = data?.total_items || 0;

    const handleSort = (column: keyof FeedbackListItem) => {
        if (loading) return;
        const newSortAsc = sortBy === column ? !sortAsc : true;
        setSortBy(column);
        setSortAsc(newSortAsc);
        updateSearchParams({ sortBy: column, sortAsc: newSortAsc });
    };

    const applySearch = () => {
        const newSearch = searchInput.trim().toLowerCase();
        setSearch(newSearch);
        setPage(1);
        updateSearchParams({ search: newSearch, page: 1 });
    };

    const clearSearch = () => {
        setSearchInput("");
        setSearch("");
        setPage(1);
        updateSearchParams({ search: "", page: 1 });
    };

    const handleRefresh = () => {
        fetchData();
    };

    const openFeedbackDetails = (feedbackId: string) => {
        if (!loading) {
            navigate(`/administration/feedback/${feedbackId}?${searchParams.toString()}`);
        }
    };

    const openFeedbackDetailsInNewTab = (feedbackId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        if (!loading) {
            window.open(`/#/administration/feedback/${feedbackId}?${searchParams.toString()}`, "_blank");
        }
    };

    const openExportModal = () => {
        setExportModalOpen(true);
        setExportStatus("idle");
        setExportError("");
        setModalStartDate("");
        setModalEndDate("");
    };

    const closeExportModal = () => {
        if (exportStatus === "loading") return;
        setExportModalOpen(false);
        setExportStatus("idle");
        setExportError("");
        setModalStartDate("");
        setModalEndDate("");
    };

    const cancelExport = () => {
        if (exportStatus === "loading") {
            setExportStatus("idle");
        }
    };

    const handleModalExport = async () => {
        try {
            setExportStatus("loading");
            setExportError("");

            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available for exporting feedback");

            const startDateStr = modalStartDate || undefined;
            const endDateStr = modalEndDate || undefined;

            const blob = await exportFeedbackCsvApi(idToken, startDateStr, endDateStr, userId, TimezoneUtils.getUserTimezone());

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "feedback_export.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);

            setExportStatus("success");
        } catch (err) {
            console.error("Failed to export feedback", err);
            setExportError(err instanceof Error ? err.message : "Failed to export feedback");
            setExportStatus("error");
        }
    };

    const dateRangeLabel = formatDateRange();
    const filtersActive = Boolean(search || dateRangeLabel || reviewStatusFilter !== "all" || failureTypeFilter.length > 0);
    const exportDisabled = loading || exportStatus === "loading";

    const renderSortIcon = (column: keyof FeedbackListItem) => {
        const isActive = sortBy === column;
        const style = { opacity: isActive ? 1 : 0.25, transition: "opacity 120ms ease" } as const;
        if (!isActive || sortAsc) {
            return <MdArrowUpward size={14} style={style} />;
        }
        return <MdArrowDownward size={14} style={style} />;
    };

    const renderSortableHeader = (columnKey: keyof FeedbackListItem, label: string) => (
        <UnstyledButton
            onClick={() => handleSort(columnKey)}
            disabled={loading}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: sortBy === columnKey ? "var(--mantine-color-blue-6)" : "var(--mantine-color-dark-6)",
                fontWeight: 600
            }}
        >
            <Text fw={600} size="sm">
                {label}
            </Text>
            {renderSortIcon(columnKey)}
        </UnstyledButton>
    );

    const getTypeBadge = (type: string) => {
        const normalized = type?.toLowerCase();
        switch (normalized) {
            case "like":
            case "positive":
                return (
                    <Badge color="green" variant="light" size="sm" leftSection={<MdThumbUpOffAlt size={14} />}>
                        Positive
                    </Badge>
                );
            case "dislike":
            case "negative":
                return (
                    <Badge color="red" variant="light" size="sm" leftSection={<MdThumbDownOffAlt size={14} />}>
                        Negative
                    </Badge>
                );
            default:
                return (
                    <Badge color="blue" variant="light" size="sm">
                        {type || "General"}
                    </Badge>
                );
        }
    };

    const getTargetBadge = (target: string) => {
        const normalized = target?.toLowerCase();
        switch (normalized) {
            case "asset":
                return (
                    <Badge color="grape" variant="light" size="sm">
                        Asset
                    </Badge>
                );
            case "singleanswer":
            case "single_answer":
                return (
                    <Badge color="blue" variant="light" size="sm">
                        Single Answer
                    </Badge>
                );
            case "conversation":
                return (
                    <Badge color="orange" variant="light" size="sm">
                        Conversation
                    </Badge>
                );
            default:
                return <div></div>;
        }
    };

    const getStatusBadge = (status?: string | null) => {
        if (status === "reviewed") {
            return (
                <Badge color="teal" variant="light" size="sm" leftSection={<MdCheckCircle size={12} />}>
                    Reviewed
                </Badge>
            );
        }
        return (
            <Badge color="gray" variant="light" size="sm" leftSection={<MdHourglassEmpty size={12} />}>
                Not Reviewed
            </Badge>
        );
    };

    const renderCell = (columnKey: keyof FeedbackListItem, item: FeedbackListItem) => {
        switch (columnKey) {
            case "name":
                return (
                    <Group gap="sm" justify="space-between" wrap="nowrap">
                        <Stack gap={2} maw={360} style={{ flex: 1 }}>
                            <Text fw={600}>{item.name || "Anonymous"}</Text>
                            <Text size="xs" c="dimmed">
                                {item.username}
                            </Text>
                        </Stack>
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={e => openFeedbackDetailsInNewTab(item.id, e)}
                            disabled={loading}
                            title="Open in new tab"
                            style={{ alignSelf: "flex-start" }}
                        >
                            <MdOpenInNew size={16} />
                        </ActionIcon>
                    </Group>
                );
            case "domain":
                return <DomainBadge domain={item.domain} />;
            case "feedback_target":
                return getTargetBadge(item.feedback_target);
            case "feedback_type":
                return getTypeBadge(item.feedback_type);
            case "timestamp":
                return <TimezoneDateDisplay timestamp={item.timestamp} showTime={true} showTimezone={true} timezoneAsTooltip={true} size="sm" />;
            case "review_status":
                return item.feedback_type === "dislike" ? getStatusBadge(item.review_status) : null;
            case "failure_type":
                if (item.feedback_type !== "dislike" || !item.failure_type) return null;
                return (
                    <Badge color="violet" variant="light" size="sm">
                        {item.failure_type}
                    </Badge>
                );
            case "reason":
                if (item.feedback_type !== "dislike" || !item.reason) return null;
                return (
                    <Badge color="orange" variant="light" size="sm">
                        {REASON_LABELS[item.reason] ?? item.reason}
                    </Badge>
                );
            default:
                return <Text size="sm">{String(item[columnKey] ?? "")}</Text>;
        }
    };

    // Hide user column when filtering by specific user
    const visibleColumns = userId ? FEEDBACK_COLUMNS.filter(col => col.key !== "name") : FEEDBACK_COLUMNS;

    return (
        <Paper
            withBorder
            radius="lg"
            shadow="sm"
            p={compact ? "md" : "lg"}
            style={{
                display: "flex",
                flexDirection: "column",
                gap: rem(compact ? 16 : 24),
                flex: 1,
                minHeight: compact ? "20rem" : "28rem",
                overflow: "hidden"
            }}
        >
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                <Stack gap={8} maw={520}>
                    <Title order={compact ? 4 : 3} size={compact ? "1.2rem" : "1.4rem"}>
                        {title}
                    </Title>
                    <Group gap="xs">
                        <Badge color="blue" variant="light" size="sm" radius="sm">
                            {totalItems.toLocaleString()} total
                        </Badge>
                        {filtersActive && (
                            <Badge color="grape" variant="light" size="sm" radius="sm">
                                Filters active
                            </Badge>
                        )}
                        {dateRangeLabel && (
                            <Badge color="gray" variant="outline" size="sm" radius="sm">
                                {dateRangeLabel}
                            </Badge>
                        )}
                    </Group>
                    {!compact && (
                        <Text size="sm" c="dimmed">
                            Review customer sentiment, follow-ups, and export snapshots when needed.
                        </Text>
                    )}
                </Stack>
                <Group gap="sm">
                    {showRefresh && (
                        <Button variant="subtle" size="sm" leftSection={<MdRefresh size={16} />} onClick={handleRefresh} disabled={loading}>
                            Refresh
                        </Button>
                    )}
                    {showExport && (
                        <Button variant="subtle" size="sm" leftSection={<MdFileDownload size={16} />} onClick={openExportModal} disabled={exportDisabled}>
                            Export
                        </Button>
                    )}
                </Group>
            </Group>

            {(showSearch || showDateFilter) && (
                <Flex gap="sm" wrap="wrap" align="center">
                    {showSearch && (
                        <>
                            <TextInput
                                placeholder="Search for feedback"
                                leftSection={<MdSearch size={16} />}
                                rightSection={
                                    searchInput && (
                                        <ActionIcon onClick={clearSearch} variant="subtle" size="sm">
                                            <MdClose size={14} />
                                        </ActionIcon>
                                    )
                                }
                                value={searchInput}
                                disabled={exportDisabled}
                                onChange={e => setSearchInput(e.currentTarget.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") applySearch();
                                }}
                                style={{ flex: "1 1 260px", minWidth: "220px" }}
                            />
                            <Button onClick={applySearch} disabled={exportDisabled} variant="filled" leftSection={<MdSearch size={16} />}>
                                Search
                            </Button>
                        </>
                    )}

                    <Select
                        value={reviewStatusFilter}
                        onChange={val => {
                            const newVal: ReviewStatusFilter = isValidReviewStatusFilter(val) ? val : "all";
                            setReviewStatusFilter(newVal);
                            setPage(1);
                            updateSearchParams({ reviewStatus: newVal === "all" ? "" : newVal, page: 1 });
                        }}
                        data={[
                            { value: "all", label: "All statuses" },
                            { value: "not_reviewed", label: "Not Reviewed" },
                            { value: "reviewed", label: "Reviewed" }
                        ]}
                        disabled={loading}
                        style={{ minWidth: "150px" }}
                    />

                    <MultiSelect
                        value={failureTypeFilter}
                        onChange={vals => {
                            setFailureTypeFilter(vals.filter(isFailureType));
                            setPage(1);
                            updateSearchParams({ failureTypes: vals.length > 0 ? vals.join(",") : "", page: 1 });
                        }}
                        data={[...FAILURE_TYPES]}
                        placeholder="Failure type"
                        clearable
                        disabled={loading}
                        style={{ minWidth: "180px" }}
                    />

                    {showDateFilter && (
                        <Popover opened={datePickerOpened} onClose={() => setDatePickerOpened(false)} width={280} position="bottom-start" withArrow shadow="md">
                            <Popover.Target>
                                <Button
                                    variant="light"
                                    size="sm"
                                    leftSection={<MdCalendarToday size={16} />}
                                    disabled={loading}
                                    onClick={() => {
                                        setDatePickerOpened(!datePickerOpened);
                                        if (!datePickerOpened) {
                                            const [startDate, endDate] = dateRange;
                                            setTempStartDate(startDate);
                                            setTempEndDate(endDate);
                                        }
                                    }}
                                >
                                    {dateRangeLabel ? "Adjust range" : "Date range"}
                                </Button>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Stack gap="sm">
                                    <DatePickerInput
                                        label="Start date"
                                        size="sm"
                                        value={tempStartDate}
                                        maxDate={tempEndDate || new Date()}
                                        onChange={date => {
                                            if (date) {
                                                setTempStartDate(typeof date === "string" ? new Date(date) : date);
                                            } else {
                                                setTempStartDate(null);
                                            }
                                        }}
                                        clearable={false}
                                    />
                                    <DatePickerInput
                                        label="End date"
                                        size="sm"
                                        value={tempEndDate}
                                        minDate={tempStartDate || undefined}
                                        maxDate={new Date()}
                                        onChange={date => {
                                            if (date) {
                                                setTempEndDate(typeof date === "string" ? new Date(date) : date);
                                            } else {
                                                setTempEndDate(null);
                                            }
                                        }}
                                        clearable={false}
                                    />
                                    <Group justify="space-between" gap="xs">
                                        <Button variant="subtle" size="xs" onClick={clearDateRange}>
                                            Clear
                                        </Button>
                                        <Button variant="filled" size="xs" onClick={applyDateRange}>
                                            Apply
                                        </Button>
                                    </Group>
                                </Stack>
                            </Popover.Dropdown>
                        </Popover>
                    )}
                </Flex>
            )}

            <Box pos="relative" style={{ flex: 1, minHeight: compact ? 200 : 280 }}>
                <LoadingOverlay visible={loading} />

                {error ? (
                    <LoadErrorAlert title="Couldn't load feedback" error={error} onRetry={fetchData} />
                ) : feedback.length === 0 && !loading ? (
                    <Center h="100%" mih={compact ? 150 : 220}>
                        <Stack gap="xs" align="center">
                            <MdOutlineFeedback size={36} color="var(--mantine-color-gray-5)" />
                            <Text fw={600}>No feedback entries</Text>
                            <Text size="sm" c="dimmed" ta="center">
                                {userId ? "This user hasn't provided any feedback yet." : "Try adjusting the search keywords or date range filters."}
                            </Text>
                        </Stack>
                    </Center>
                ) : (
                    <ScrollArea.Autosize mah={maxHeight} type="auto">
                        <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ fontSize: "0.9rem" }}>
                            <TableThead style={{ backgroundColor: "var(--mantine-color-gray-0)" }}>
                                <TableTr>
                                    {visibleColumns.map(column => (
                                        <TableTh key={column.label} style={{ minWidth: column.width }}>
                                            {column.sortable === false ? (
                                                <Text fw={600} size="sm">
                                                    {column.label}
                                                </Text>
                                            ) : (
                                                renderSortableHeader(column.key, column.label)
                                            )}
                                        </TableTh>
                                    ))}
                                </TableTr>
                            </TableThead>
                            <TableTbody>
                                {feedback.map(item => (
                                    <TableTr key={item.id} onClick={() => openFeedbackDetails(item.id)} style={{ cursor: loading ? "default" : "pointer" }}>
                                        {visibleColumns.map(column => (
                                            <TableTd key={column.label} style={{ minWidth: column.width }}>
                                                {renderCell(column.key, item)}
                                            </TableTd>
                                        ))}
                                    </TableTr>
                                ))}
                            </TableTbody>
                        </Table>
                    </ScrollArea.Autosize>
                )}
            </Box>

            <Group justify="space-between" mt="sm">
                <Select
                    value={String(pageSize)}
                    onChange={val => {
                        if (val) {
                            const newPageSize = Number(val);
                            setPageSize(newPageSize);
                            setPage(1);
                            updateSearchParams({ pageSize: newPageSize, page: 1 });
                        }
                    }}
                    data={[
                        { value: "10", label: "10 / page" },
                        { value: "15", label: "15 / page" },
                        { value: "25", label: "25 / page" },
                        { value: "50", label: "50 / page" },
                        { value: "100", label: "100 / page" }
                    ]}
                    w={150}
                    disabled={loading}
                />
                <Pagination
                    total={totalPages}
                    value={page}
                    onChange={newPage => {
                        setPage(newPage);
                        updateSearchParams({ page: newPage });
                    }}
                    disabled={loading}
                />
            </Group>

            {showExport && (
                <Modal
                    opened={exportModalOpen}
                    onClose={closeExportModal}
                    title="Export feedback"
                    size="md"
                    closeOnClickOutside={exportStatus !== "loading"}
                    closeOnEscape={exportStatus !== "loading"}
                >
                    <Stack gap="md">
                        {exportStatus === "idle" && (
                            <>
                                <Text size="sm" c="dimmed">
                                    Provide an optional date range to filter the export. Leave the fields empty to export all feedback entries.
                                </Text>
                                <Group>
                                    <div>
                                        <Text size="sm" fw={500} mb={2}>
                                            Start date
                                        </Text>
                                        <TextInput
                                            type="date"
                                            value={modalStartDate}
                                            onChange={event => setModalStartDate(event.currentTarget.value)}
                                            style={{ minWidth: "140px" }}
                                        />
                                    </div>
                                    <div>
                                        <Text size="sm" fw={500} mb={2}>
                                            End date
                                        </Text>
                                        <TextInput
                                            type="date"
                                            value={modalEndDate}
                                            onChange={event => setModalEndDate(event.currentTarget.value)}
                                            style={{ minWidth: "140px" }}
                                        />
                                    </div>
                                </Group>
                                <Group justify="flex-end" mt="md">
                                    <Button variant="subtle" onClick={closeExportModal}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleModalExport} leftSection={<MdFileDownload size={16} />}>
                                        Export
                                    </Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "loading" && (
                            <>
                                <Center py="xl">
                                    <Stack gap="md" align="center">
                                        <Loader size="lg" />
                                        <Text size="sm">Preparing export...</Text>
                                        <Text size="xs" c="dimmed">
                                            This may take a moment
                                        </Text>
                                    </Stack>
                                </Center>
                                <Group justify="center">
                                    <Button variant="subtle" onClick={cancelExport}>
                                        Cancel
                                    </Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "success" && (
                            <>
                                <Center py="xl">
                                    <Stack gap="sm" align="center">
                                        <MdFileDownload size={32} color="var(--mantine-color-green-6)" />
                                        <Text fw={600}>Export complete!</Text>
                                        <Text size="sm" c="dimmed">
                                            The excel file has been downloaded to your device.
                                        </Text>
                                    </Stack>
                                </Center>
                                <Group justify="center">
                                    <Button onClick={closeExportModal}>Close</Button>
                                </Group>
                            </>
                        )}

                        {exportStatus === "error" && (
                            <>
                                <Alert color="red" title="Export failed">
                                    {exportError}
                                </Alert>
                                <Group justify="flex-end" mt="md">
                                    <Button variant="subtle" onClick={closeExportModal}>
                                        Close
                                    </Button>
                                    <Button onClick={handleModalExport} leftSection={<MdFileDownload size={16} />}>
                                        Try again
                                    </Button>
                                </Group>
                            </>
                        )}
                    </Stack>
                </Modal>
            )}
        </Paper>
    );
};
